import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Descriptions, Space, Spin, Table, Tabs, Tag, Typography, notification,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import lib from '../../../assets/utils/lib';

const { Text } = Typography;

const FIELD_LABELS = {
  plan_size: 'Plan size',
  account_size: 'Account size',
  premium_type: 'Premium type',
  plan: 'Plan / package',
  status: 'Status',
};

const formatFieldLabel = (key) => FIELD_LABELS[key] || String(key).replace(/_/g, ' ');

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function ChangelogSection({ title, items, emptyText, renderItem }) {
  if (!items?.length) {
    return <Text type="secondary">{emptyText}</Text>;
  }
  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Text strong>{title} ({items.length})</Text>
      {items.map((item, idx) => (
        <div key={item.row ?? item.policy_no ?? item.name ?? idx} style={{ fontSize: 13 }}>
          {renderItem(item, idx)}
        </div>
      ))}
    </Space>
  );
}

function ChangelogDetail({ record }) {
  const changelog = record?.changelog ?? {};
  const summary = record?.summary ?? {};

  return (
    <div style={{ padding: '8px 0' }}>
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Total rows">{summary.total_rows ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Valid rows">{summary.valid_rows ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Inserted">{summary.inserted ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Updated">{summary.updated ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Failed">{summary.failed ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Clean existing">
          {summary.clean_existing ? 'Yes' : 'No'}
        </Descriptions.Item>
      </Descriptions>

      <Tabs
        size="small"
        items={[
          {
            key: 'new',
            label: `New (${changelog.new?.length ?? 0})`,
            children: (
              <ChangelogSection
                title="New enrollees"
                items={changelog.new}
                emptyText="No new records in this upload."
                renderItem={(item) => (
                  <Text>
                    {item.name || '—'}
                    {item.phone ? ` · ${item.phone}` : ''}
                    {item.premium_type ? ` · ${item.premium_type}` : ''}
                    {item.insurance_package ? ` · ${item.insurance_package}` : ''}
                  </Text>
                )}
              />
            ),
          },
          {
            key: 'returned',
            label: `Returned (${changelog.returned?.length ?? 0})`,
            children: (
              <ChangelogSection
                title="Returned members"
                items={changelog.returned}
                emptyText="No returned members in this upload."
                renderItem={(item) => (
                  <Text>
                    Row {item.row}: {item.name || '—'}
                    {item.policy_no ? ` (${item.policy_no})` : ''}
                    {' — '}
                    status {item.previous_status} → {item.current_status}
                  </Text>
                )}
              />
            ),
          },
          {
            key: 'changed',
            label: `Changed (${changelog.changed?.length ?? 0})`,
            children: (
              <ChangelogSection
                title="Changed fields"
                items={changelog.changed}
                emptyText="No field changes in this upload."
                renderItem={(item) => (
                  <div>
                    <Text>
                      Row {item.row}: {item.name || '—'}
                      {item.policy_no ? ` (${item.policy_no})` : ''}
                    </Text>
                    {item.fields && (
                      <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                        {Object.entries(item.fields).map(([key, diff]) => (
                          <li key={key}>
                            <Text type="secondary">
                              {formatFieldLabel(key)}: {String(diff?.old ?? '—')} → {String(diff?.new ?? '—')}
                            </Text>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

const OrgBulkUploadLogsPanel = ({ socialNo, orgName }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0 });
  const [downloadingId, setDownloadingId] = useState(null);

  const loadLogs = useCallback(async (page = 1, limit = 10) => {
    if (!socialNo) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.listCorpBulkUploadLogs(socialNo, { page, limit }));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Failed to load upload history' });
        setRows([]);
        return;
      }
      const data = parsed.data;
      const list = Array.isArray(data?.rows)
        ? data.rows
        : (Array.isArray(data) ? data : []);
      setRows(list);
      const m = data?.meta ?? parsed.meta ?? {};
      setMeta({
        page: Number(m.page ?? page),
        limit: Number(m.limit ?? limit),
        total: Number(m.total ?? list.length),
      });
    } catch (e) {
      notification.error({ message: e?.message || 'Network error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [socialNo]);

  useEffect(() => {
    loadLogs(1, meta.limit);
  }, [socialNo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async (record) => {
    if (!record?.output_file) return;
    setDownloadingId(record.id);
    try {
      const filename = String(record.output_file).split('/').pop();
      await lib.downloadFileWithAuth(record.output_file, filename);
      notification.success({ message: 'Download started' });
    } catch (e) {
      notification.error({ message: e?.message || 'Download failed' });
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      width: 160,
      render: (v) => formatDateTime(v),
    },
    {
      title: 'Summary',
      key: 'summary',
      render: (_, row) => {
        const s = row.summary ?? {};
        const c = s.changelog ?? row.changelog ?? {};
        return (
          <Space size={4} wrap>
            <Tag>+{s.inserted ?? 0}</Tag>
            <Tag color="processing">~{s.updated ?? 0}</Tag>
            {(s.failed ?? 0) > 0 && <Tag color="error">{s.failed} failed</Tag>}
            {(c.new_count ?? c.new?.length) != null && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                new {c.new_count ?? c.new?.length ?? 0}
                {' · '}
                returned {c.returned_count ?? c.returned?.length ?? 0}
                {' · '}
                changed {c.changed_count ?? c.changed?.length ?? 0}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Rows',
      key: 'rows',
      width: 100,
      render: (_, row) => row.summary?.total_rows ?? '—',
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, row) => (
        row.output_file ? (
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            loading={downloadingId === row.id}
            onClick={() => handleDownload(row)}
          >
            Output CSV
          </Button>
        ) : null
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Bulk upload audit"
        description={
          orgName
            ? `Review HR bulk CSV uploads for ${orgName}. Expand a row for new, returned, and changed member details.`
            : 'Review HR bulk CSV uploads. Expand a row for changelog details.'
        }
      />
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={() => loadLogs(meta.page, meta.limit)} disabled={loading}>
          Refresh
        </Button>
      </Space>
      <Spin spinning={loading}>
        <Table
          rowKey={(r) => r.id ?? r.created_at}
          columns={columns}
          dataSource={rows}
          expandable={{
            expandedRowRender: (record) => <ChangelogDetail record={record} />,
            rowExpandable: (record) => Boolean(record?.changelog || record?.summary),
          }}
          pagination={{
            current: meta.page,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => loadLogs(page, pageSize),
          }}
          locale={{ emptyText: 'No bulk upload history for this organization' }}
        />
      </Spin>
    </div>
  );
};

export default OrgBulkUploadLogsPanel;
